import classNames from 'classnames';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import FJResourceItemLoading from '../../common/components/FJResourceItemLoading/FJResourceItemLoading';
import FJRetry from '../../common/components/FJRetry/FJRetry';
import FJConstant from '../../constants/FJConstant';
import FJCoreConfig from '../../core/FJCoreConfig';
import FJProjectStore from '../../store/FJProjectStore';
import { type FJDetailsVideoData, FJDetailsPageAction } from '../../redux/action/FJDetailsPageAction';
import { useAppDispatch } from '../../redux/hooks';
import { GENERATE_STATE } from '../../constants/FJEnumConstant';
import FJVideoPreviewPlayer from '../FJVideoPreviewPlayer/FJVideoPreviewPlayer';
import type { VideoPreviewShortCardEaContext } from '../FJVideoPreviewPlayer/FJVideoPreviewPlayer';
import styles from './FJDetailsMediaPanel.module.less';

export type FJDetailsVideoLoadPhase = 'loading' | 'ready' | 'error';

interface FJDetailsMediaPanelProps {
    /** 与 Redux / FJProjectStore 中片段 id 一致，对应一个 FJDetailManager */
    detailId: string;
    videoData: FJDetailsVideoData;

    /** 与详情条 `exportRatio` / `RATIO_LIST` 项一致，驱动预览画布比例 */
    exportRatio?: string;
    /** 填满父级高度（详情卡固定 400px 主体） */
    fillHeight?: boolean;
    /** 预览加载阶段：供父级禁用点赞/工具条等 */
    onVideoLoadPhaseChange?: (phase: FJDetailsVideoLoadPhase) => void;
    /** 注册当前预览实例的暂停函数，供卡片在点赞/工具条操作前调用 */
    onPausePreviewReady?: (pause: () => void) => void;
    /** 用户点击播放时退出多选 */
    onPlayStart?: () => void;
    /** 详情短视频卡片播放器埋点（播放/暂停 click、播完 finish） */
    shortCardEa?: VideoPreviewShortCardEaContext;
}

const DEFAULT_DETAILS_PREVIEW_RATIO = '9:16';
/** 合并轮询：等待 `getManager` 就绪后再读 `getSourceInfo`；间隔与总时长上限共用 */
const DETAILS_MEDIA_POLL_MS = 80;
const DETAILS_MEDIA_POLL_MAX_MS = 60_000;
/** 预览始终无法就绪（断网、`init` 失败等）时，在骨架屏一段时间后改显 FJRetry */
const DETAILS_MEDIA_STALL_SHOW_RETRY_MS = 15_000;

function readSourceMeta(manager: NonNullable<ReturnType<typeof FJProjectStore.getManager>>): {
    duration: number;
    width: number;
    height: number;
} {
    try {
        const info = manager.getVideoElement().getSourceInfo() as {
            duration?: number;
            width?: number;
            height?: number;
        };
        return {
            duration: info?.duration ?? 0,
            width: info?.width ?? 1920,
            height: info?.height ?? 1080,
        };
    } catch {
        return { duration: 0, width: 1920, height: 1080 };
    }
}

/** 解析导出比例；`original` 在源尺寸可用时返回源宽高比 */
function getRatioWH(previewRatio: string, sourceW: number, sourceH: number): { rw: number; rh: number } | null {
    if (previewRatio === 'original') {
        if (sourceW > 0 && sourceH > 0) {
            return { rw: sourceW, rh: sourceH };
        }
        return null;
    }
    const parts = previewRatio.split(':').map(Number);
    if (
        parts.length < 2 ||
        !Number.isFinite(parts[0]) ||
        !Number.isFinite(parts[1]) ||
        (parts[0] ?? 0) <= 0 ||
        (parts[1] ?? 0) <= 0
    ) {
        return null;
    }
    return { rw: parts[0]!, rh: parts[1]! };
}

function isLandscapeRatioWH(rw: number, rh: number): boolean {
    return rw > rh;
}

export default function FJDetailsMediaPanel({
    detailId,
    videoData,
    exportRatio,
    fillHeight,
    onVideoLoadPhaseChange,
    onPausePreviewReady,
    onPlayStart,
    shortCardEa,
}: FJDetailsMediaPanelProps) {
    const dispatch = useAppDispatch();
    const { startTime, endTime, thumbnailUrl, thumbnailUrlStatus } = videoData;
    /** 仅在上传成功后使用接口首帧图作为静态封面，避免未就绪占位图误判 */
    const detailPosterThumbnailUrl =
        thumbnailUrlStatus === GENERATE_STATE.SUCCEEDED && thumbnailUrl?.trim() ? thumbnailUrl.trim() : '';

    type DetailManager = NonNullable<ReturnType<typeof FJProjectStore.getManager>>;
    /** 轮询到手的 manager（仅当与当前 `detailId` 一致时参与合并，避免换条时短暂沿用旧实例） */
    const [managerPolled, setManagerPolled] = useState<{ id: string; m: DetailManager } | null>(null);
    /** 每帧同步读 store（`init` resolve 后即存在）；与异步轮询结果二选一合并 */
    const managerFromStore = FJProjectStore.getManager(detailId);
    const manager = managerFromStore ?? (managerPolled?.id === detailId ? managerPolled.m : undefined);

    /** 源视频元数据：时长与分辨率，用于比例计算与 `FJVideoPreviewPlayer`；缺省为常见竖屏兜底 */
    const [sourceMeta, setSourceMeta] = useState({ duration: 0, width: 1920, height: 1080 });
    /** 与 `retryNonce` 联动：重试后重新计时、清除误报的 error */
    const [mediaLoadError, setMediaLoadError] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);

    /** 将当前片段的 `FJDetailManager` 包装为预览播放器所需 API；`manager` 变则重建 */
    const previewApi = useMemo(() => (manager ? FJProjectStore.createPreviewApi(manager) : undefined), [manager]);

    /**
     * 合并轮询：① `getManager` 仅在 `FJProjectStore.init` 内各 `mgr.init()` 完成后才有值，需 `setManagerPolled` 触发渲染；
     * ② `getSourceInfo` 在首帧前可能 `duration === 0`。`setTimeout(0)` 后同一 interval 内先 tick 再定时，
     * 最长 `DETAILS_MEDIA_POLL_MAX_MS`；无 URL 时写兜底并停止。
     */
    useEffect(() => {
        let cancelled = false;
        let pollIv: number | undefined;
        let maxWait: number | undefined;

        const boot = window.setTimeout(() => {
            if (cancelled) return;

            const tick = () => {
                if (cancelled) return;
                const m = FJProjectStore.getManager(detailId);
                if (!m) {
                    return;
                }
                if (!managerFromStore) {
                    setManagerPolled(prev => (prev?.id === detailId && prev.m === m ? prev : { id: detailId, m }));
                }

                try {
                    const st = m.getVideoElement().getSourceStatus();
                    if (st === FJConstant.ERROR) {
                        setMediaLoadError(true);
                    }
                } catch {
                    /* ignore */
                }

                const meta = readSourceMeta(m);
                if (meta.duration > 0) {
                    setSourceMeta(meta);
                    if (pollIv != null) window.clearInterval(pollIv);
                    if (maxWait != null) window.clearTimeout(maxWait);
                }
            };

            pollIv = window.setInterval(tick, DETAILS_MEDIA_POLL_MS);
            tick();
            maxWait = window.setTimeout(() => {
                if (pollIv != null) window.clearInterval(pollIv);
            }, DETAILS_MEDIA_POLL_MAX_MS);
        }, 0);

        return () => {
            cancelled = true;
            window.clearTimeout(boot);
            if (pollIv != null) window.clearInterval(pollIv);
            if (maxWait != null) window.clearTimeout(maxWait);
        };
    }, [detailId, managerFromStore]);

    const usePreviewPlayer = Boolean(manager && previewApi && sourceMeta.duration > 0);

    useEffect(() => {
        setMediaLoadError(false);
    }, [detailId]);

    useEffect(() => {
        if (usePreviewPlayer) {
            setMediaLoadError(false);
        }
    }, [usePreviewPlayer]);

    /** 长时间无可用预览（常见：断网导致 `FJProjectStore.init` 未写入 manager）→ 显示重试 */
    useEffect(() => {
        if (usePreviewPlayer || mediaLoadError) return;
        const t = window.setTimeout(() => {
            setMediaLoadError(true);
        }, DETAILS_MEDIA_STALL_SHOW_RETRY_MS);
        return () => window.clearTimeout(t);
    }, [detailId, usePreviewPlayer, mediaLoadError, retryNonce]);

    useEffect(() => {
        if (!onVideoLoadPhaseChange) return;
        const phase: FJDetailsVideoLoadPhase = usePreviewPlayer ? 'ready' : mediaLoadError ? 'error' : 'loading';
        onVideoLoadPhaseChange(phase);
    }, [usePreviewPlayer, mediaLoadError, onVideoLoadPhaseChange, detailId]);

    const onRetryVideoLoad = () => {
        setMediaLoadError(false);
        setRetryNonce(n => n + 1);
        void dispatch(FJDetailsPageAction.retryDetailsSessionVideoAction());
    };

    const previewRatio = exportRatio?.trim() || DEFAULT_DETAILS_PREVIEW_RATIO;

    /** 将导出比例键（含 `original`）与源宽高解析为 `{ rw, rh }`，供横竖版与 `aspectRatio` 使用 */
    const ratioWH = useMemo(
        () => getRatioWH(previewRatio, sourceMeta.width, sourceMeta.height),
        [previewRatio, sourceMeta.width, sourceMeta.height],
    );

    const isLandscapeLayout = ratioWH == null || isLandscapeRatioWH(ratioWH.rw, ratioWH.rh);

    /** 竖版时给外层列设置 `aspectRatio`，横版不干预（由子级 max 宽度等约束） */
    const columnStyle = useMemo((): CSSProperties | undefined => {
        if (isLandscapeLayout || !ratioWH) return undefined;
        return { aspectRatio: `${ratioWH.rw} / ${ratioWH.rh}` };
    }, [isLandscapeLayout, ratioWH]);

    const previewMaxSide = FJCoreConfig.VIDEO_PREVIEW_MAX_LONG_EDGE;

    return (
        <div
            className={classNames(
                styles.root,
                fillHeight && styles.root_fill_height,
                isLandscapeLayout ? styles.col_media_landscape : styles.col_media_tall,
            )}
            style={columnStyle}
        >
            <div className={styles.player_chrome}>
                {/* <div className={styles.player_chrome_blur} aria-hidden /> */}
                <div className={styles.stage}>
                    {usePreviewPlayer ? (
                        <FJVideoPreviewPlayer
                            layout='fill'
                            playbackInstanceId={detailId}
                            onPauseControlReady={onPausePreviewReady}
                            onPlayStart={onPlayStart}
                            thumbnailUrl={detailPosterThumbnailUrl}
                            duration={sourceMeta.duration}
                            startTime={startTime}
                            endTime={endTime}
                            previewRatio={previewRatio}
                            sourceWidth={sourceMeta.width}
                            sourceHeight={sourceMeta.height}
                            previewApi={previewApi!}
                            previewMaxSide={previewMaxSide}
                            shortCardEa={shortCardEa}
                        />
                    ) : mediaLoadError ? (
                        <div className={styles.load_error_wrap}>
                            <FJRetry isShow className={styles.load_error_retry} refreshCallback={onRetryVideoLoad} />
                        </div>
                    ) : (
                        <div className={styles.loading} role='status' aria-live='polite' aria-busy='true'>
                            <FJResourceItemLoading className={styles.loading_skeleton} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
